import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import { FaEye, FaEdit, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Select from 'react-select';

// States list for station creation
const INDIAN_STATES = [
  'Andaman and Nicobar Islands',
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chandigarh',
  'Chhattisgarh',
  'Dadra and Nagar Haveli',
  'Daman and Diu',
  'Delhi',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jammu and Kashmir',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Ladakh',
  'Lakshadweep',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Puducherry',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Bhutan',
  'Nepal',
  'Bangladesh'
];

// Clean form data utility function
const cleanFormData = (data) => {
  const cleanedData = {};
  
  // Required fields (always include)
  const requiredFields = ['name', 'phoneNumber', 'station', 'createdBy'];
  requiredFields.forEach(field => {
    cleanedData[field] = data[field];
  });

  return cleanedData;
};

// Custom styles for react-select
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '42px',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '14px',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    '&:hover': {
      borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
    }
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
    color: state.isSelected ? 'white' : '#374151',
    fontSize: '14px',
    padding: '8px 12px'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#374151',
    fontSize: '14px'
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9ca3af',
    fontSize: '14px'
  })
};

// Options for select fields
const pageSizeOptions = [
  { value: 5, label: '5 per page' },
  { value: 10, label: '10 per page' },
  { value: 20, label: '20 per page' },
  { value: 50, label: '50 per page' }
];

const MAManagement = () => {
  const { user } = useContext(AuthContext);

  // Reference to "name" input for focusing on edit
  const nameRef = useRef(null);

  // -----------------------
  // FORM STATE (Add/Edit)
  // -----------------------
  const [form, setForm] = useState({
    name: '',
    phoneNumber: '',
    station: '',
    createdBy: user?.user?._id,
  });
  const [stations, setStations] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Station creation states
  const [showCreateStation, setShowCreateStation] = useState(false);
  const [stationForm, setStationForm] = useState({
    name: '',
    state: '',
    createdBy: user?.user?._id
  });
  const [isCreatingStation, setIsCreatingStation] = useState(false);

  // -----------------------
  // MAs + PAGINATION
  // -----------------------
  const [mas, setMas] = useState([]);
  const [filter, setFilter] = useState('');
  const [pageSize, setPageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalMAs, setTotalMAs] = useState(0);

  // -----------------------
  // MODAL FOR VIEWING ONE MA
  // -----------------------
  const [selectedMA, setSelectedMA] = useState(null);

  // -----------------------
  // FETCH STATIONS
  // -----------------------
  useEffect(() => {
    fetchStations();
  }, []);

  const fetchStations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/stations`);
      console.log('Fetched stations:', response.data);
      setStations(response.data);
    } catch (error) {
      console.error('Error fetching stations:', error);
      Swal.fire('Error', 'Failed to fetch stations', 'error');
    }
  };

  // Station creation functions
  const resetStationForm = () => {
    setStationForm({
      name: '',
      state: '',
      createdBy: user?.user?._id
    });
  };

  const handleStationFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'state') {
      setStationForm(prev => ({ ...prev, [name]: value }));
    } else {
      setStationForm(prev => ({ ...prev, [name]: value.toUpperCase() }));
    }
  };

  const validateStationForm = () => {
    if (!stationForm.name.trim()) {
      Swal.fire('Error', 'Station name is required', 'error');
      return false;
    }
    if (!stationForm.state.trim()) {
      Swal.fire('Error', 'State is required', 'error');
      return false;
    }
    return true;
  };

  const handleCreateStation = async () => {
    if (!validateStationForm()) return;

    setIsCreatingStation(true);
    try {
      const cleanedStationData = cleanFormData(stationForm);
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/stations`, cleanedStationData);
      
      console.log('Station created successfully:', response.data);
      
      // Add the new station to the stations list
      setStations(prev => [...prev, response.data]);
      
      // Select the newly created station in the main form
      setForm(prev => ({ ...prev, station: response.data._id }));
      
      // Close the modal and reset form
      setShowCreateStation(false);
      resetStationForm();
      
      Swal.fire({
        title: 'Success!',
        text: 'Station created successfully',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error creating station:', error);
      Swal.fire('Error', error.response?.data?.error || 'Failed to create station', 'error');
    } finally {
      setIsCreatingStation(false);
    }
  };

  // -----------------------
  // FETCH MAs
  // (Whenever currentPage, pageSize, or filter changes)
  // -----------------------
  useEffect(() => {
    fetchMAs();
  }, [currentPage, pageSize, filter]);

  const fetchMAs = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/mas`, {
        params: {
          page: currentPage,
          limit: pageSize,
          filter: filter,
          userCompanyId: user?.user?.company
        }
      });
      console.log('Fetched MAs:', response.data);
      setMas(response.data.mas);
      setTotalMAs(response.data.totalMAs);
    } catch (error) {
      console.error('Error fetching MAs:', error);
      Swal.fire('Error', 'Failed to fetch Master Accounts', 'error');
    }
  };

  // -----------------------
  // FORM VALIDATION
  // -----------------------
  const validateForm = () => {
    if (!form.name?.trim()) {
      Swal.fire('Error', 'Master Account name is required', 'error');
      return false;
    }

    if (!form.phoneNumber?.trim()) {
      Swal.fire('Error', 'Phone number is required', 'error');
      return false;
    }

    // Phone number validation
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(form.phoneNumber.trim())) {
      Swal.fire('Error', 'Please enter a valid 10-digit phone number starting with 6-9', 'error');
      return false;
    }

    if (!form.station) {
      Swal.fire('Error', 'Station is required', 'error');
      return false;
    }

    return true;
  };

  // -----------------------
  // ADD OR UPDATE MA
  // -----------------------
  const handleAddOrUpdateMA = async () => {
    if (!validateForm()) return;

    try {
      const cleanedData = cleanFormData(form);
      
      if (isEditing) {
        cleanedData.updatedBy = user?.user?._id;
        const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/mas/${editingId}`, cleanedData);
        console.log('MA updated successfully:', response.data);
        Swal.fire('Success', 'Master Account updated successfully!', 'success');
      } else {
        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/mas`, cleanedData);
        console.log('MA added successfully:', response.data);
        Swal.fire('Success', 'Master Account added successfully!', 'success');
      }
      
      resetForm();
      fetchMAs();
    } catch (error) {
      console.error('Error saving MA:', error);
      Swal.fire('Error', error.response?.data?.error || 'Failed to save Master Account', 'error');
    }
  };

  // -----------------------
  // EDIT MA
  // -----------------------
  const startEditing = (ma) => {
    setIsEditing(true);
    setEditingId(ma._id);
    
    const formData = {
      name: ma.name || '',
      phoneNumber: ma.phoneNumber || '',
      station: ma.station?._id || '',
      createdBy: ma.createdBy || user?.user?._id,
    };
    
    setForm(formData);
    
    // Focus on name input after a short delay
    setTimeout(() => {
      if (nameRef.current) {
        nameRef.current.focus();
      }
    }, 100);
  };

  // -----------------------
  // VIEW MA (Modal)
  // -----------------------
  const handleViewMA = (ma) => {
    setSelectedMA(ma);
  };

  const closeViewMAModal = () => {
    setSelectedMA(null);
  };

  // -----------------------
  // UTILS
  // -----------------------
  const resetForm = () => {
    setForm({
      name: '',
      phoneNumber: '',
      station: '',
      createdBy: user?.user?._id,
    });
    setIsEditing(false);
    setEditingId(null);
  };

  const totalPages = Math.ceil(totalMAs / pageSize);

  // Handle input changes with uppercase conversion for name
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      setForm({ ...form, [name]: value.toUpperCase() });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Master Account Management</h1>
                <p className="text-gray-600">Manage Master Accounts in your system</p>
              </div>
              <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-2">
                <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
                  <span className="text-blue-700 font-medium">Total Master Accounts: {totalMAs}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center">
              <span className="bg-blue-100 text-blue-600 rounded-full p-2 mr-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </span>
              {isEditing ? 'Edit Master Account' : 'Add New Master Account'}
            </h2>
            {isEditing && (
              <button
                onClick={resetForm}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
                Cancel Edit
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Name */}
            <div className="flex flex-col">
              <label className="text-gray-700 font-semibold mb-1">
                Master Account Name <span className="text-red-500">*</span>
              </label>
              <input
                ref={nameRef}
                type="text"
                name="name"
                value={form.name}
                onChange={handleInputChange}
                className="border rounded p-2 w-full focus:outline-blue-500"
                placeholder="Enter Master Account name"
              />
            </div>

            {/* Phone Number */}
            <div className="flex flex-col">
              <label className="text-gray-700 font-semibold mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={handleInputChange}
                className="border rounded p-2 w-full focus:outline-blue-500"
                placeholder="Enter 10-digit phone number"
                maxLength="10"
              />
            </div>

            {/* Station */}
            <div className="flex flex-col">
              <label className="text-gray-700 font-semibold mb-1">
                Station <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Select
                      name="station"
                      value={stations.find(station => station._id === form.station) ? 
                        { value: form.station, label: stations.find(station => station._id === form.station)?.name } : null}
                      onChange={(selectedOption) => {
                        console.log('Station selected:', selectedOption);
                        if (selectedOption?.value === 'CREATE_NEW') {
                          resetStationForm();
                          setShowCreateStation(true);
                        } else {
                          setForm({ ...form, station: selectedOption?.value || '' });
                        }
                      }}
                      options={(() => {
                        const stationOptions = [
                          ...stations.map(station => ({ value: station._id, label: station.name })),
                          { 
                            value: 'CREATE_NEW', 
                            label: '➕ Create New Station'
                          }
                        ];
                        console.log('Station options:', stationOptions);
                        return stationOptions;
                      })()}
                      styles={customSelectStyles}
                      placeholder="Select Station"
                      isSearchable
                      isClearable
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      resetStationForm();
                      setShowCreateStation(true);
                    }}
                    className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center min-w-[42px] shadow-sm"
                    title="Create New Station"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                    </svg>
                  </button>
                </div>
                {stations.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded z-10">
                    <button
                      type="button"
                      onClick={() => setShowCreateStation(true)}
                      className="text-green-600 hover:text-green-700 text-sm font-medium px-4 py-2 border border-green-600 rounded-lg hover:bg-green-50"
                    >
                      + Create First Station
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleAddOrUpdateMA}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              {isEditing ? 'Update Master Account' : 'Add Master Account'}
            </button>
          </div>
        </div>

        {/* Filter and Pagination Controls */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search Master Accounts by name or phone..."
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <svg
                  className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            
            <Select
              value={pageSizeOptions.find(option => option.value === pageSize)}
              onChange={(selectedOption) => {
                setPageSize(selectedOption.value);
                setCurrentPage(1);
              }}
              options={pageSizeOptions}
              styles={customSelectStyles}
              className="w-full sm:w-1/3 md:w-1/4"
              isSearchable={false}
            />
          </div>
        </div>

        {/* Master Account List (Desktop Table) */}
        <div className="overflow-x-auto hidden lg:block">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Master Account ID</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Station</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Created By</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {mas?.map((ma, index) => (
                  <tr key={ma._id} className={`hover:bg-blue-50 transition-colors duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">{ma.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">{ma.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{ma.phoneNumber}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{ma.station?.name || 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <div>
                        <div className="font-medium">{ma.createdBy?.name || 'N/A'}</div>
                        {ma.company?.hasSeparateCRM === false && ma.company?.name && (
                          <div className="text-xs text-gray-500">{ma.company.name}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEditing(ma)}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-2 rounded-lg hover:from-blue-600 hover:to-blue-700 flex items-center gap-1 transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          <FaEdit className="text-xs" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleViewMA(ma)}
                          className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-3 py-2 rounded-lg hover:from-gray-600 hover:to-gray-700 flex items-center gap-1 transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          <FaEye className="text-xs" />
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Master Account List (Mobile Cards) */}
        <div className="lg:hidden space-y-4">
          {mas?.map((ma) => (
            <div key={ma._id} className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-gray-900 text-lg">{ma.name}</h3>
                  <p className="text-blue-600 font-medium text-sm">{ma.id}</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => startEditing(ma)}
                    className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600 transition-colors"
                  >
                    <FaEdit className="text-xs" />
                  </button>
                  <button
                    onClick={() => handleViewMA(ma)}
                    className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600 transition-colors"
                  >
                    <FaEye className="text-xs" />
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center text-gray-600">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {ma.phoneNumber}
                </div>
                <div className="flex items-center text-gray-600">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {ma.station?.name || 'N/A'}
                </div>
                <div className="flex items-center text-gray-600">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {ma.createdBy?.name || 'N/A'}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FaChevronLeft className="w-3 h-3 mr-1" />
                Previous
              </button>
              
              <div className="flex items-center space-x-1">
                {[...Array(totalPages)].map((_, index) => {
                  const page = index + 1;
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg ${
                          currentPage === page
                            ? 'text-blue-600 bg-blue-50 border border-blue-300'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-700'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 2 ||
                    page === currentPage + 2
                  ) {
                    return (
                      <span key={page} className="px-2 py-2 text-gray-500">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <FaChevronRight className="w-3 h-3 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Station Creation Modal */}
      {showCreateStation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md mx-auto relative shadow-2xl">
            <div className="bg-gradient-to-r from-green-600 to-emerald-700 text-white px-6 py-4 rounded-t-lg flex justify-between items-center">
              <h2 className="text-xl font-bold">Create New Station</h2>
              <button
                className="text-white hover:text-gray-200 focus:outline-none"
                onClick={() => setShowCreateStation(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex flex-col">
                  <label className="text-gray-700 font-semibold mb-1">
                    Station Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={stationForm.name}
                    onChange={handleStationFormChange}
                    className="border rounded p-3 w-full focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Enter station name"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-gray-700 font-semibold mb-1">
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="state"
                    value={stationForm.state}
                    onChange={handleStationFormChange}
                    className="border rounded p-3 w-full focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Select State</option>
                    {INDIAN_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowCreateStation(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                  disabled={isCreatingStation}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateStation}
                  disabled={isCreatingStation}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {isCreatingStation ? 'Creating...' : 'Create Station'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Master Account Detail Modal */}
      {selectedMA && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-2 md:p-4">
          <div className="bg-white rounded-lg w-full max-w-lg md:max-w-xl mx-auto relative shadow-2xl transform transition-all duration-300 ease-in-out max-h-[90vh] md:max-h-[85vh] flex flex-col">
            {/* Modal header with gradient - Fixed */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-4 md:px-6 py-3 md:py-4 rounded-t-lg flex justify-between items-center flex-shrink-0">
              <h2 className="text-lg md:text-xl font-bold flex items-center">
                <span className="bg-white text-blue-600 rounded-full p-1 mr-2 md:mr-3 shadow-md">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-6 md:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </span>
                Master Account Details
              </h2>
              <button
                className="text-white hover:text-gray-200 focus:outline-none transition-colors rounded-full hover:bg-white/20 p-1"
                onClick={closeViewMAModal}
                aria-label="Close"
              >
                <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal content - Scrollable */}
            <div className="overflow-y-auto flex-1 p-4 md:p-6">
              <div className="grid grid-cols-1 gap-3 md:gap-4">
                {/* Personal Information - Compact */}
                <div className="bg-blue-50 rounded-lg p-3 border-l-4 border-blue-500">
                  <h3 className="font-medium text-blue-800 mb-2 text-xs uppercase tracking-wide">Personal Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="sm:col-span-2">
                      <p className="text-xs text-gray-500">Master Account Name</p>
                      <p className="font-medium text-gray-800 text-sm">{selectedMA.name}</p>
                    </div>
                  </div>
                </div>
                
                {/* Contact Information - Compact */}
                <div className="bg-purple-50 rounded-lg p-3 border-l-4 border-purple-500">
                  <h3 className="font-medium text-purple-800 mb-2 text-xs uppercase tracking-wide">Contact Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="sm:col-span-2">
                      <p className="text-xs text-gray-500">Phone Number</p>
                      <p className="font-medium text-gray-800 text-sm flex items-center">
                        <svg className="w-3 h-3 mr-1 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {selectedMA.phoneNumber}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Location Information - Compact */}
                <div className="bg-green-50 rounded-lg p-3 border-l-4 border-green-500">
                  <h3 className="font-medium text-green-800 mb-2 text-xs uppercase tracking-wide">Location Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="sm:col-span-2">
                      <p className="text-xs text-gray-500">Station</p>
                      <p className="font-medium text-gray-800 text-sm">
                        {selectedMA.station?.name || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* System Information - Compact */}
                <div className="bg-indigo-50 rounded-lg p-3 border-l-4 border-indigo-500">
                  <h3 className="font-medium text-indigo-800 mb-2 text-xs uppercase tracking-wide">System Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <p className="text-xs text-gray-500">Created By</p>
                      <p className="font-medium text-gray-800 text-sm flex items-center">
                        <svg className="w-3 h-3 mr-1 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {selectedMA.createdBy?.name || 'N/A'}
                      </p>
                    </div>
                    {selectedMA.company?.hasSeparateCRM === false && selectedMA.company?.name && (
                      <div>
                        <p className="text-xs text-gray-500">Company</p>
                        <p className="font-medium text-gray-800 text-sm flex items-center">
                          <svg className="w-3 h-3 mr-1 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          {selectedMA.company.name}
                        </p>
                      </div>
                    )}
                    <div className={selectedMA.company?.hasSeparateCRM === false && selectedMA.company?.name ? '' : 'sm:col-span-2'}>
                      <p className="text-xs text-gray-500">Master Account ID</p>
                      <p className="font-medium text-gray-800 text-sm">{selectedMA.id}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MAManagement;